import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/Modal';

const SERVICE_TYPES = ['Oil & Filter', 'MOT', 'Tyre Replacement', 'Brake Service', 'Full Service', 'Hydraulic Service', 'Annual Inspection', 'Battery', 'Air Filter', 'Other'];

export default function AddMaintenanceModal() {
  const { modals, closeModal, vehicles, addMaintRecord, showToast } = useApp();
  const open   = modals['add-maintenance'];
  const preVid = modals['add-maintenance-vid'] || '';

  const [vid, setVid]       = useState('');
  const [type, setType]     = useState('');
  const [nextKm, setNextKm] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [notes, setNotes]   = useState('');

  useEffect(() => {
    if (open) {
      setVid(preVid);
      setType('');
      setNextKm('');
      setNextDate('');
      setNotes('');
    }
  }, [open, preVid]);

  const vehicle = vehicles.find(v => v.id === vid);
  const currentKm = vehicle?.odometer || 0;

  function submit() {
    if (!vid || !type) { showToast('Please select a vehicle and service type'); return; }
    if (!nextKm && !nextDate) { showToast('Please set at least one due trigger (KM or date)'); return; }

    addMaintRecord({
      id: 'MS-' + Date.now(),
      vid,
      icon: vehicle?.icon || '🔧',
      veh: vehicle?.name || vid,
      type,
      date: '',
      km: '',
      odometerKm: null,
      cost: null,
      garage: '',
      next: nextKm
        ? `${parseInt(nextKm).toLocaleString()} km`
        : nextDate
          ? new Date(nextDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
          : '—',
      nextKm: nextKm ? parseInt(nextKm) : null,
      nextDate: nextDate || null,
      triggerType: nextKm ? 'km' : 'date',
      notifyAt: nextKm ? Math.max(0, parseInt(nextKm) - 2000) : null,
      notifNote: '',
      notes: notes || '',
      status: 'scheduled',
      notified: false,
    });

    closeModal('add-maintenance');
    showToast(`✓ ${type} scheduled for ${vehicle?.name}`);
    setVid(''); setType(''); setNextKm(''); setNextDate(''); setNotes('');
  }

  if (!open) return null;

  return (
    <Modal open={!!open} onClose={() => closeModal('add-maintenance')}>
      <ModalHeader
        title="Schedule Maintenance"
        subtitle="Set up a service with due trigger"
        onClose={() => closeModal('add-maintenance')}
      />
      <ModalBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="fg">
              <label className="fl">Vehicle</label>
              <select className="fsel2" value={vid} onChange={e => setVid(e.target.value)}>
                <option value="">— select —</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.icon} {v.name}</option>)}
              </select>
            </div>
            <div className="fg">
              <label className="fl">Service type</label>
              <select className="fsel2" value={type} onChange={e => setType(e.target.value)}>
                <option value="">— select —</option>
                {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Current KM — read-only from vehicle profile */}
          {vehicle && (
            <div style={{
              padding: '10px 14px', background: 'var(--sur2)', border: '1px solid var(--bd)',
              borderRadius: 'var(--r)', display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 20 }}>{vehicle.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 600 }}>{vehicle.name}</div>
                <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{vehicle.plate} · {vehicle.type}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Current Odometer</div>
                <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'DM Mono',monospace", color: 'var(--tx)' }}>
                  {currentKm.toLocaleString()} <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--tx3)' }}>km</span>
                </div>
              </div>
            </div>
          )}

          {/* Due triggers */}
          <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4 }}>Due Trigger <span style={{ fontWeight: 400, color: 'var(--tx3)' }}>(whichever comes first)</span></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="fg">
              <label className="fl">Due at KM</label>
              <input type="number" className="fi" value={nextKm} onChange={e => setNextKm(e.target.value)}
                placeholder={currentKm ? `e.g. ${(currentKm + 10000).toLocaleString()}` : 'e.g. 130000'} />
              {nextKm && currentKm > 0 && (
                <div style={{ fontSize: 9, color: parseInt(nextKm) <= currentKm ? 'var(--ac)' : 'var(--gr)', marginTop: 2 }}>
                  {parseInt(nextKm) > currentKm
                    ? `${(parseInt(nextKm) - currentKm).toLocaleString()} km remaining`
                    : '⚠️ Already past due!'}
                </div>
              )}
            </div>
            <div className="fg">
              <label className="fl">Due by date</label>
              <input type="date" className="fi" value={nextDate} onChange={e => setNextDate(e.target.value)} />
              {nextDate && (
                <div style={{ fontSize: 9, color: new Date(nextDate) < new Date() ? 'var(--ac)' : 'var(--gr)', marginTop: 2 }}>
                  {new Date(nextDate) > new Date()
                    ? `${Math.ceil((new Date(nextDate) - new Date()) / 86400000)} days away`
                    : '⚠️ Already past due!'}
                </div>
              )}
            </div>
          </div>

          <div className="fg">
            <label className="fl">Notes</label>
            <textarea className="fi" value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="e.g. Replace all 4 tyres — check alignment too" />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost" onClick={() => closeModal('add-maintenance')}>Cancel</button>
        <button className="btn btn-primary" onClick={submit}>📅 Schedule Service</button>
      </ModalFooter>
    </Modal>
  );
}
